import { useState } from 'react'
import { demoApi } from '../services/api'
import { showError } from '../utils/showError'
import { PageHeader, Button, FormField, Card } from '../components/common'

const DemoGenerator = () => {
  const [formData, setFormData] = useState({
    buildingName: '',
    buildingUsername: '',
    adminName: '',
    adminEmail: '',
    floorCount: 1,
    unitsPerFloor: 1
  })
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [cleanupResult, setCleanupResult] = useState(null)
  const [cleanupLoading, setCleanupLoading] = useState(false)

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setResult(null)

    try {
      const response = await demoApi.generateDemo(formData)
      setResult(response.data)
    } catch (err) {
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to cleanup all demo data? This action cannot be undone.')) {
      return
    }

    setCleanupLoading(true)
    setCleanupResult(null)

    try {
      const response = await demoApi.cleanup()
      setCleanupResult(response.data)
    } catch (err) {
      showError(err)
    } finally {
      setCleanupLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Demo Generator" 
        subtitle="Generate a demo building with units and admin user"
        action={
          <Button
            onClick={handleCleanup}
            disabled={cleanupLoading}
            loading={cleanupLoading}
            variant="danger"
          >
            {cleanupLoading ? 'Cleaning up...' : 'Cleanup Demo Data'}
          </Button>
        }
      />

      <div className="max-w-4xl">
        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormField
              label="Building Name"
              name="buildingName"
              value={formData.buildingName}
              onChange={handleInputChange}
              placeholder="Enter building name"
              required
            />

            <FormField
              label="Building Username"
              name="buildingUsername"
              value={formData.buildingUsername}
              onChange={handleInputChange}
              placeholder="Enter building username"
              required
            />

            <FormField
              label="Admin Name"
              name="adminName"
              value={formData.adminName}
              onChange={handleInputChange}
              placeholder="Enter admin name"
              required
            />

            <FormField
              label="Admin Email"
              name="adminEmail"
              type="email"
              value={formData.adminEmail}
              onChange={handleInputChange}
              placeholder="Enter admin email"
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Floor Count"
                name="floorCount"
                type="number"
                value={formData.floorCount}
                onChange={handleInputChange}
                min="1"
                max="50"
                required
              />
              
              <FormField
                label="Units per Floor"
                name="unitsPerFloor"
                type="number"
                value={formData.unitsPerFloor}
                onChange={handleInputChange}
                min="1"
                max="20"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              loading={loading}
              variant="primary"
              fullWidth
            >
              {loading ? 'Generating...' : 'Generate Demo'}
            </Button>
          </form>

          {result && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Demo Generated Successfully!</h3>
              <div className="text-green-700 space-y-1">
                <p><strong>Building ID:</strong> {result.buildingId}</p>
                <p><strong>Building Name:</strong> {result.buildingName}</p>
                <p><strong>Total Users:</strong> {result.totalUsers}</p>
                <p><strong>Total Units:</strong> {result.totalUnits}</p>
                <p><strong>Total Floors:</strong> {result.totalFloors}</p>
              </div>
            </div>
          )}

          {cleanupResult && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Demo Data Cleaned Up Successfully!</h3>
              <div className="text-blue-700 space-y-1">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Users:</strong> {cleanupResult.deletedUsers}</p>
                    <p><strong>Buildings:</strong> {cleanupResult.deletedBuildings}</p>
                    <p><strong>Building Units:</strong> {cleanupResult.deletedBuildingUnits}</p>
                    <p><strong>Building Members:</strong> {cleanupResult.deletedBuildingMembers}</p>
                    <p><strong>Join Requests:</strong> {cleanupResult.deletedBuildingJoinRequests}</p>
                    <p><strong>Addresses:</strong> {cleanupResult.deletedAddresses}</p>
                  </div>
                  <div>
                    <p><strong>Deliveries:</strong> {cleanupResult.deletedDeliveries}</p>
                    <p><strong>Recipients:</strong> {cleanupResult.deletedRecipients}</p>
                    <p><strong>User Messages:</strong> {cleanupResult.deletedUserMessages}</p>
                    <p><strong>Message States:</strong> {cleanupResult.deletedMessageStates}</p>
                    <p><strong>User Devices:</strong> {cleanupResult.deletedUserDevices}</p>
                    <p><strong>User Settings:</strong> {cleanupResult.deletedUserSettings}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p><strong>Execution Time:</strong> {cleanupResult.executionTime}</p>
                  {cleanupResult.deletedUserIds && cleanupResult.deletedUserIds.length > 0 && (
                    <p><strong>Deleted User IDs:</strong> {cleanupResult.deletedUserIds.length} users</p>
                  )}
                  {cleanupResult.deletedBuildingIds && cleanupResult.deletedBuildingIds.length > 0 && (
                    <p><strong>Deleted Building IDs:</strong> {cleanupResult.deletedBuildingIds.length} buildings</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default DemoGenerator